import React, { useEffect, useState } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register ONLY core plugins globally (NOT datalabels)
ChartJS.register(ArcElement, Tooltip, Legend);

// ---------- Shared look & feel ----------
const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  padding: 15px;

  /* Fill parent card */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 20px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  align-items: center;
  min-height: 34px;
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

/* Chart area that expands to fill remaining space */
const ChartBox = styled.div`

  padding: 12px;

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;

/* Give the canvas explicit 100% height */
const CanvasWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;
// ---------------------------------------

const monthOptions = [
  { value: "ALL", label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

const Top10PurchaseShare = ({ data }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("ALL");
  const [chartData, setChartData] = useState(null);

  const calculatePurchaseValue = (row) => parseFloat(row.K_42) || 0;

  useEffect(() => {
    if (!data || data.length === 0) return;
    const years = Array.from(new Set(data.map((d) => parseInt(d.Rok, 10)))).sort((a, b) => b - a);
    setAvailableYears(years);
    if (!selectedYear) setSelectedYear(years[0]);
  }, [data]);

  useEffect(() => {
    if (!data || !selectedYear) return;

    const filtered = data.filter((d) => {
      const year = parseInt(d.Rok, 10);
      const month = parseInt(d.Miesiac, 10);
      return year === selectedYear && (selectedMonth === "ALL" || month === selectedMonth);
    });

    const totals = {};
    filtered.forEach((row) => {
      const name = row.NazwaDostawcy || "Nieznany";
      const value = calculatePurchaseValue(row);
      totals[name] = (totals[name] || 0) + value;
    });

    const sorted = Object.entries(totals).sort((a, b) => b[1] - a[1]).slice(0, 10);
    if (sorted.length === 0) { setChartData(null); return; }

    const labels = sorted.map(([name]) => (name.length > 15 ? name.slice(0, 15) + "…" : name));
    const values = sorted.map(([, val]) => val);

    const bgColors = [
      "#9848c7", "#4ac784", "#f0ca4d", "#e94b4b", "#4b89e9",
      "#ff8c00", "#2ec4b6", "#ff6f91", "#7fdbda", "#b4975a"
    ];

    setChartData({
      labels,
      datasets: [{
        label: "Udział w zakupach",
        data: values,
        backgroundColor: bgColors,
        borderColor: "#fff",
        borderWidth: 2,
      }],
    });
  }, [data, selectedYear, selectedMonth]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Zakupy top 10 dostawców
        <InfoIconWrapper data-tooltip-id="zakupy-share-tooltip">
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>

      <InfoTooltip
        id="zakupy-share-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Udział w zakupach</strong><br />
            Pokazuje 10 dostawców o największym udziale<br />
            w wartości zakupów dla wybranego okresu.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok:&nbsp;</label>
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => <option key={year} value={year}>{year}</option>)}
          </Select>
        </div>
        <div>
          <label>Miesiąc:&nbsp;</label>
          <Select
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(e.target.value === "ALL" ? "ALL" : parseInt(e.target.value, 10))
            }
          >
            {monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <ChartBox>
          <CanvasWrap>
            {/* datalabels enabled only for this chart */}
            <Pie
              key={`${selectedYear}-${selectedMonth}`} // avoid canvas reuse warnings
              data={chartData}
              plugins={[ChartDataLabels]}
              options={{
                responsive: true,
                maintainAspectRatio: false, // let canvas use full height
                layout: { padding: 0 },
                plugins: {
                  legend: { position: "bottom" },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || "";
                        const value = context.raw;
                        const total = context.dataset.data.reduce((sum, v) => sum + v, 0);
                        const percentage = total ? ((value / total) * 100).toFixed(1) : "0.0";
                        return `${label}: ${value.toLocaleString("pl-PL")} PLN (${percentage}%)`;
                      },
                    },
                  },
                  datalabels: {
                    color: "#fff",
                    font: { size: 11, weight: "bold" },
                    formatter: (value, ctx) => {
                      const arr = ctx?.dataset?.data || [];
                      const total = arr.reduce((a, b) => a + b, 0);
                      return total ? `${((value / total) * 100).toFixed(1)}%` : "";
                    },
                    clip: false,
                  },
                },
              }}
            />
          </CanvasWrap>
        </ChartBox>
      ) : (
        <p style={{ textAlign: "center" }}>Brak danych dla wybranych filtrów.</p>
      )}
    </ChartWrapper>
  );
};

export default Top10PurchaseShare;
